import { Col, Container, Row } from "react-grid-system";
import IPassword from "../../../models/password/IPassword";
import useStyleUtils from "../../../utilities/styles";
import IMFAToken from "../../../models/password/IMFAToken";
import { Button, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, tokens } from "@fluentui/react-components";
import { TOTP } from "totp-generator";
import { useState, useEffect, CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { CopyFilled } from "@fluentui/react-icons";

const Notes = (props: { notes: string }) => {
    const { t } = useTranslation();
    const style = useStyleUtils();

    return (
        <Row className={style.mb3}>
            <Col xs={12}>
                <div>
                    <strong>{t("passwords.notes")}</strong>
                </div>
                <div style={{ whiteSpace: "pre-wrap" }} dangerouslySetInnerHTML={{ __html: props.notes || "" }} />
            </Col>
        </Row>
    );
};

const totpBackground = (countdown: number): CSSProperties => {
    const color = tokens.colorPaletteLightTealBackground2;
    const gradientWidth = (countdown / 30) * 100;

    return {
        background: `linear-gradient(to right, ${color} 0, ${color} ${gradientWidth}%, transparent ${gradientWidth}%, transparent 100%)`,
    };
};

const Token = (props: { token: IMFAToken, countdown: number }) => {
    const { token } = props;

    const style = useStyleUtils();

    let key = token.token
        .replaceAll(" ", "")
        .replaceAll("-", "");

    const { otp } = TOTP.generate(key, { digits: 6, period: 30 });

    const handleCopyClicked = () => navigator.clipboard.writeText(otp);

    return (
        <TableRow>
            <TableCell>{token.name}</TableCell>
            <TableCell style={totpBackground(props.countdown)}>
                <div className={`${style.dFlex} ${style.alignItemsCenter} ${style.justifyContentBetween}`}>
                    <span className={style.dInlineBlock}>
                        {otp}
                    </span>
                    <Button size="small" icon={<CopyFilled />} onClick={handleCopyClicked} />
                </div>
            </TableCell>
        </TableRow>
    );
};

const Tokens = (props: { mfaTokens: IMFAToken[], countdown: number }) => {
    const { t } = useTranslation();
    const style = useStyleUtils();

    return (
        <Table className={style.w100}>
            <TableHeader>
                <TableRow>
                    <TableHeaderCell>{t("passwords.mfa.table.headers.name")}</TableHeaderCell>
                    <TableHeaderCell>{t("passwords.mfa.table.headers.TOTP")}</TableHeaderCell>
                </TableRow>
            </TableHeader>
            <TableBody>
                {
                    props.mfaTokens.map((token, index) => <Token token={token} countdown={props.countdown} key={index} />)
                }
            </TableBody>
        </Table>
    );
};

export default function MFA(props: { password: IPassword }) {
    const { t } = useTranslation();
    
    const mfa = props.password.mfa!;
    const [countdown, setCountdown] = useState<number>(30); // Change initial countdown to 30

    useEffect(() => {
        const interval = setInterval(() => {
            const now = new Date();
            const seconds = now.getSeconds();
            const remaining = 30 - (seconds % 30); // Reset every 30 seconds
            setCountdown(remaining);

            if (remaining === 30 || remaining === 0) {
                // Logic to regenerate TOTP
                // For example, refresh tokens or trigger a state update
            }
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    return (
        <Container fluid>
            {
                mfa.notes &&
                <Notes notes={mfa.notes} />
            }
            {
                mfa.tokens.length > 0 &&
                <Row>
                    <Col xs={12}>
                        <div>
                            <strong>{t("passwords.mfa.tokens.header")}</strong>
                        </div>
                        <Tokens mfaTokens={mfa.tokens} countdown={countdown} />
                    </Col>
                </Row>
            }
        </Container>
    );
}